import "./stats-dashboard.scss";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import { S2sDashboardPeriod } from "@TXQueries";
import classNames from "classnames";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LoadingDots } from "@client/components";
import { StatsDashboardData } from "@client/components/orders-chart/interfaces";
import { OrdersChart } from "@client/components/orders-chart/orders-chart";
import Stat from "@client/components/reusable/stat/stat";
import SvgIcon from "@client/components/reusable/SvgIcons";
import { ReactComponent as ChartIcon } from "@client/components/reusable/SvgIcons/library/chart.svg";
import StatsProducts from "@client/components/stats-products/stats-products";
import { useRootContext } from "@client/context/RootContext";

export const StatsDashboardIcon = () => (
	<Link key={"dashboard-top"} to={"/dashboard"} className="stats-dashboard-btn">
		<SvgIcon
			component={ChartIcon}
			width="24"
			height="32"
			style={{ stroke: "white", strokeWidth: 2 }}
		/>
	</Link>
);

export const StatsDashboard: React.FC = () => {
	const { apiService } = useRootContext();
	const { t } = useTranslation();

	const [selectedTimeframe, setSelectedTimeframe] =
		useState<S2sDashboardPeriod>("yesterday");

	const [isLoadingData, setIsLoadingData] = useState(true);

	const [data, setData] = useState<StatsDashboardData>(null);

	useEffect(() => {
		(async () => {
			setIsLoadingData(true);
			const response = await apiService.api("/vendor/dashboard", {
				selectedTimeframe,
			});

			if (response.data) {
				const d = response.data;
				setData(d);
			}
			setIsLoadingData(false);
		})();
	}, [selectedTimeframe]);

	return data == null ? (
		<LoadingDots />
	) : (
		<ThemeProvider theme={createTheme()}>
			<div className="stats-wrapper">
				<div className="stats-orders">
					<div className="stats-orders-right-area">
						<OrdersChart
							isLoadingData={isLoadingData}
							data={data.chart}
							selectedTimeframe={selectedTimeframe}
							setSelectedTimeframe={setSelectedTimeframe}
						/>

						<div className="stats-data">
							<div className="stats-data-orders">
								<Stat
									value={data.orders.open}
									label={t("stats-dashboard.data-section.open-orders")}
								/>
								<Stat
									value={data.orders.openMoreThan24h}
									label={t(
										"stats-dashboard.data-section.open-for-more-than-24h"
									)}
									changeType={
										data.orders.openMoreThan24h_threshold == 1
											? "decrease"
											: "no-change"
									}
								/>
								<Stat
									value={
										data.orders.averageTimeHours +
										" " +
										t("stats-dashboard.data-section.order-time-average-hours")
									}
									label={`${t(
										"stats-dashboard.data-section.order-time-average"
									)}`}
								/>
							</div>

							<div className="stats-data-skus">
								<Stat
									value={data.skus.active}
									label={t("stats-dashboard.data-section.active-skus")}
								/>
								<Stat
									value={data.skus.nonActive}
									label={t("stats-dashboard.data-section.non-active-skus")}
								/>
							</div>
						</div>
					</div>

					<div className="stats-last7days">
						<div className="stats-last7days-data">
							<Stat
								label={t("stats-dashboard.percentages.late")}
								value={data.last7days.late + "%"}
								changeType={
									data.last7days.late_threshold == 1 ? "decrease" : "increase"
								}
							/>
							<Stat
								label={t("stats-dashboard.percentages.rejected")}
								value={data.last7days.rejected + "%"}
								changeType={
									data.last7days.rejected_threshold == 1
										? "decrease"
										: "increase"
								}
							/>

							<Stat
								label={t("stats-dashboard.percentages.return-items")}
								value={data.last7days.return_items_percent + "%"}
								changeType={
									data.last7days.returnItems_threshold == 1
										? "decrease"
										: "increase"
								}
							/>
						</div>

						<div className="stats-last7days-stock">
							<div
								className={classNames("stats-last7days-stock-label", {
									"stats-last7days-stock-label-old":
										dayjs().diff(dayjs(data.laststockupdate), "day") >=
										data.laststockupdate_threshold,
								})}
							>
								{t("stats-dashboard.percentages.last-stock-update")}
							</div>
							{dayjs(data.laststockupdate).format("DD/MM HH:mm")}
						</div>
					</div>
				</div>

				<StatsProducts products={data.products} />
			</div>
		</ThemeProvider>
	);
};
